import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useBpV2State } from "../../../context/BusinessPlanV2Context";
import { GlobalState } from "../../../context/GlobalContext";
import { AuthState } from "context/AuthContext";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Chip from "@mui/material/Chip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Tooltip from "@mui/material/Tooltip";
import { grey, green, red } from "@mui/material/colors";
import { KeyboardBackspaceRounded } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";

import SeasonInfoPanel from "./ManualProjection/SeasonInfoPanel";
import SalesProjectionTable, { MonthValueCell } from "./ManualProjection/SalesProjectionTable";
import CategoryView from "./CategoryView/CategoryView";
import NumberTextField from "../../ReusableComponents/NumberTextField";
import "../businessPlanV2.css";

import { calculateStoreAge } from "../../../utils/calculateStoreAge";
import { roundDecimalValueUptoTwo } from "../../../utils/roundDecimalValue";
import { calculateAsp, calculateDiscount, calculateRos, calculateST } from "../../../utils/calculateStoreData";

import {
	API,
	divisionData,
	getSeasonMonths,
	round1dp,
	canApproveByHierarchy,
	BP_HIERARCHY_ROLES,
	normalizeBpHierarchyRole,
	toInLakhs,
	displayNameFromEmail,
} from "../data";
import { infoPanelSx, textInfo, infoMetricValueSx } from "./storeBpTheme";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError";

const toSafeNumber = (value) => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : 0;
};

const getSafePercentage = (value, total) => {
	const safeValue = toSafeNumber(value);
	const safeTotal = toSafeNumber(total);
	if (!safeTotal) return 0;
	return roundDecimalValueUptoTwo((safeValue / safeTotal) * 100);
};

const deriveKpi = (raw) => {
	if (!raw) return null;
	const revenue = raw.nsv || 0;
	const qty = raw.qty || 0;
	const totalMrp = raw.total_mrp || 0;
	return {
		revenue,
		qty,
		asp: calculateAsp(revenue, qty),
		dis: calculateDiscount(totalMrp, revenue),
		ros: calculateRos(qty),
		dispatch: raw.dispatch_qty || 0,
		st: calculateST(raw.dispatch_qty, raw.combine_sale),
	};
};

const StoreBp = () => {
	const [searchParams] = useSearchParams();
	const submissionId = searchParams.get("submission_id");
	const {
		selectedStore,
		selectedSubmission,
		selectedPlan,
		setSelectedStore,
		setSelectedPlan,
		setSelectedSubmission,
	} = useBpV2State();
	const { setLoadingOpen, setSnackBar, snackBar, setSnackBarOpen } = GlobalState();
	const { user } = AuthState();
	const navigate = useNavigate();

	const [submissionData, setSubmissionData] = useState(null);
	const [activeDivision, setActiveDivision] = useState(divisionData[0].key);
	const [historicalData, setHistoricalData] = useState(null);
	const [allCategories, setAllCategories] = useState([]);
	const [categoriesLoading, setCategoriesLoading] = useState(false);

	const [targetData, setTargetData] = useState({});
	const [projData, setProjData] = useState({});
	const [approvalInfoAnchor, setApprovalInfoAnchor] = useState(null);
	const [projectionInfoOpen, setProjectionInfoOpen] = useState(false);
	const [gwInfoAnchor, setGwInfoAnchor] = useState(null);

	const activeSubmissionId = submissionId || selectedSubmission?.id;
	const store = selectedStore || submissionData?.store;
	const plan = selectedPlan || submissionData?.store?.plan;

	const isOwner = submissionData?.created_by === user?.email;
	const isDraft = submissionData?.status === "DRAFT";
	const isSubmitted = submissionData?.status === "SUBMITTED";
	const canEdit = isOwner && isDraft;
	const approvals = submissionData?.approvals || [];
	const hasApprovals = approvals.length > 0;
	const canUnsubmit = isOwner && isSubmitted && !submissionData?.is_final && !hasApprovals;
	const canApproveHere =
		isSubmitted &&
		!submissionData?.is_final &&
		(canApproveByHierarchy(user?.role, submissionData?.created_by_role) ||
			approvals.some((s) => s.user_email === user?.email));
	const approvedRoles = BP_HIERARCHY_ROLES.filter((role) =>
		approvals.some((a) => normalizeBpHierarchyRole(a.user_role) === role)
	);

	const showSnack = (message, severity = "error") => {
		setSnackBarOpen(true);
		setSnackBar({ ...snackBar, message, severity });
	};

	const fetchCategories = async (id) => {
		const sid = id || activeSubmissionId;
		if (!sid) return;
		try {
			setCategoriesLoading(true);
			const { data } = await axios.get(`${API.CATEGORY_VIEW}?submission_id=${sid}`);
			if (data.status === "success") {
				setAllCategories(data.result.map((c, i) => ({ ...c, id: `${c.category}-${c.division}-${i}` })));
			}
		} catch (err) {
			showSnack(parseErrMsgFromError(err));
		} finally {
			setCategoriesLoading(false);
		}
	};

	const fetchHistorical = async (storeId) => {
		if (!storeId) return;
		try {
			const { data } = await axios.post(`${API.REFRESH_HISTORICAL}/${storeId}`);
			if (data.status === "success") {
				setHistoricalData(data.result);
			}
		} catch (err) {
			console.log(parseErrMsgFromError(err));
		}
	};

	const fetchSubmission = async (id) => {
		const sid = id || activeSubmissionId;
		if (!sid) return null;
		try {
			setLoadingOpen(true);
			const { data } = await axios.get(`${API.GET_SUBMISSION}?id=${sid}`);
			if (data.status === "success") {
				const submission = data.result;
				setSubmissionData(submission);
				setSelectedSubmission(submission);
				if (submission.store) {
					setSelectedStore(submission.store);
					if (submission.store.plan) setSelectedPlan(submission.store.plan);
				}
				return submission;
			}
		} catch (err) {
			showSnack(parseErrMsgFromError(err));
			navigate("/explore/business_plan_v2");
		} finally {
			setLoadingOpen(false);
		}
		return null;
	};

	const refreshAll = async () => {
		const submission = await fetchSubmission(activeSubmissionId);
		await fetchCategories(activeSubmissionId);
		if (submission?.store?.id || store?.id) {
			await fetchHistorical(submission?.store?.id || store?.id);
		}
	};

	useEffect(() => {
		if (!submissionId) {
			navigate("/explore/business_plan_v2");
			return;
		}

		const bootstrap = async () => {
			const submission = await fetchSubmission(submissionId);
			await fetchCategories(submissionId);
			const storeId = submission?.store?.id || selectedStore?.id;
			if (storeId) await fetchHistorical(storeId);
		};

		bootstrap();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [submissionId]);

	useEffect(() => {
		const seasonWiseData = submissionData?.seasonWiseData || [];
		const newTarget = seasonWiseData.find((r) => r.is_new_target) || {};
		const projection = submissionData?.projections?.[0] || {};
		setTargetData({ ...newTarget });
		setProjData({ ...projection });
	}, [submissionData]);

	const handleSubmit = async () => {
		try {
			setLoadingOpen(true);
			await axios.put(`${API.SUBMIT}/${activeSubmissionId}`);
			showSnack("Submitted", "success");
			fetchSubmission(activeSubmissionId);
		} catch (err) {
			showSnack(parseErrMsgFromError(err));
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleCancel = async () => {
		try {
			setLoadingOpen(true);
			await axios.put(`${API.CANCEL}/${activeSubmissionId}`);
			showSnack("Cancelled — back to DRAFT", "success");
			fetchSubmission(activeSubmissionId);
		} catch (err) {
			showSnack(parseErrMsgFromError(err));
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleApproveToggle = async () => {
		if (!activeSubmissionId) return;
		try {
			const isApproved = submissionData?.approvals?.some((s) => s.user_email === user?.email);
			if (isApproved) {
				await axios.delete(`${API.APPROVAL}/${activeSubmissionId}`);
				showSnack("Approval removed", "success");
			} else {
				await axios.post(`${API.APPROVAL}/${activeSubmissionId}`);
				showSnack("Approved", "success");
			}
			fetchSubmission(activeSubmissionId);
		} catch (err) {
			showSnack(parseErrMsgFromError(err));
		}
	};

	const handleBack = () => {
		const pid = plan?.id || selectedPlan?.id || submissionData?.store?.plan?.id;
		if (pid) navigate(`/explore/business_plan_v2/plan/${pid}`);
		else navigate("/explore/business_plan_v2");
	};

	const season = plan?.season || "AW";

	const handleMonthChange = (monthKey, value) => {
		// Keep the raw string the user typed (e.g. "17." while mid-decimal) instead of
		// parseFloat-ing it immediately — coercing to a number here drops trailing/partial
		// decimal characters and re-feeds a truncated value back into the controlled input,
		// which made "17.2" render as "172" while typing.
		const updated = { ...projData, [`month_${monthKey}`]: value };
		const seasonMonths = getSeasonMonths(season);
		const total = seasonMonths.reduce((sum, m) => sum + (parseFloat(updated[`month_${m.key}`]) || 0), 0);
		updated.total_projection = round1dp(total);
		setProjData(updated);
		setTargetData((prev) => ({ ...prev, revenue: total * 100000 }));
	};

	const handleTargetFieldChange = (field, value) => {
		// Keep the raw string while typing (see handleMonthChange) so partial decimals
		// like "17." aren't coerced away mid-keystroke.
		setTargetData((prev) => ({ ...prev, [field]: value }));
	};

	const handleSaveTarget = async () => {
		try {
			setLoadingOpen(true);
			const seasonWiseData = submissionData?.seasonWiseData || [];
			const newTarget = seasonWiseData.find((r) => r.is_new_target) || {};
			const projection = submissionData?.projections?.[0] || {};
			const seasonMonths = getSeasonMonths(season);
			const sanitizedProjection = { ...projData };
			seasonMonths.forEach((m) => {
				sanitizedProjection[`month_${m.key}`] = parseFloat(sanitizedProjection[`month_${m.key}`]) || 0;
			});
			sanitizedProjection.total_projection = parseFloat(sanitizedProjection.total_projection) || 0;
			const sanitizedTarget = {
				...targetData,
				asp: parseFloat(targetData.asp) || 0,
				dis: parseFloat(targetData.dis) || 0,
			};
			await axios.put(`${API.SAVE_MANUAL_TARGET}/${activeSubmissionId}`, {
				target: { ...sanitizedTarget, id: newTarget.id },
				monthWiseProjection: { ...sanitizedProjection, id: projection.id },
			});
			showSnack("Saved", "success");
			fetchSubmission(activeSubmissionId);
		} catch (err) {
			showSnack(parseErrMsgFromError(err));
		} finally {
			setLoadingOpen(false);
		}
	};

	const seasonWiseData = submissionData?.seasonWiseData || [];
	const derivedTarget = seasonWiseData.find((r) => !r.is_new_target) || {};

	const ltlKpi = deriveKpi(historicalData?.seasonKpi?.find((r) => r.period === "LTL"));
	const lltlKpi = deriveKpi(historicalData?.seasonKpi?.find((r) => r.period === "LLTL"));
	const prevLltlKpi = deriveKpi(historicalData?.seasonKpi?.find((r) => r.period === "PREV_LLTL"));
	const sixMRaw = historicalData?.seasonKpi?.find((r) => r.period === "LAST_6M");
	const prvLtlRaw = historicalData?.seasonKpi?.find((r) => r.period === "PRV_LTL");
	const sixMKpi = deriveKpi(sixMRaw);
	const prvSixMKpi = deriveKpi(prvLtlRaw);
	const monthlyLtl = historicalData?.monthlyLtl || [];
	const monthlyLltl = historicalData?.monthlyLltl || [];
	const monthlyPrevLltl = historicalData?.monthlyPrevLltl || [];

	const repeatCustomers = sixMRaw
		? getSafePercentage(sixMRaw.repeat_customer_count, toSafeNumber(sixMRaw.repeat_customer_count) + toSafeNumber(sixMRaw.new_customer_count))
		: 0;
	const repeatCustStats = sixMRaw
		? {
				repeat: toSafeNumber(sixMRaw.repeat_customer_count),
				neu: toSafeNumber(sixMRaw.new_customer_count),
				total: toSafeNumber(sixMRaw.repeat_customer_count) + toSafeNumber(sixMRaw.new_customer_count),
				repeatPct: repeatCustomers,
				newPct: getSafePercentage(
					sixMRaw.new_customer_count,
					toSafeNumber(sixMRaw.repeat_customer_count) + toSafeNumber(sixMRaw.new_customer_count)
				),
			}
		: null;

	const growthToPrvSeason =
		sixMRaw && prvLtlRaw && prvLtlRaw.nsv
			? round1dp((((sixMRaw.nsv || 0) - prvLtlRaw.nsv) / prvLtlRaw.nsv) * 100)
			: 0;

	const formatWindowDate = (d) =>
		d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

	const gwWindowDates = (() => {
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const last6mStart = new Date(today);
		last6mStart.setDate(last6mStart.getDate() - 180);
		const prvEnd = new Date(today);
		prvEnd.setDate(prvEnd.getDate() - 180);
		const prvStart = new Date(today);
		prvStart.setDate(prvStart.getDate() - 360);
		return {
			last6m: { from: formatWindowDate(last6mStart), to: formatWindowDate(today) },
			prv6m: { from: formatWindowDate(prvStart), to: formatWindowDate(prvEnd) },
		};
	})();

	const spsf = ltlKpi
		? roundDecimalValueUptoTwo(toSafeNumber(ltlKpi.revenue) / (toSafeNumber(store?.area) || 1) / 6)
		: 0;

	const grandLtlQty = allCategories.reduce((s, r) => s + (r.ltl_qty || 0), 0);
	const divisionContribution = divisionData.reduce((acc, d) => {
		const divisionTotal = allCategories
			.filter((c) => c.division === d.key)
			.reduce((s, r) => s + (r.ltl_qty || 0), 0);
		acc[d.key] = getSafePercentage(divisionTotal, grandLtlQty);
		return acc;
	}, {});

	const hasDivisionData = allCategories.some((c) => c.division && c.division !== "NA");
	const filteredCategories = hasDivisionData
		? allCategories.filter((c) => c.division === activeDivision)
		: allCategories;

	if (!submissionId) {
		return null;
	}

	if (!store || !plan) {
		return (
			<Box p={4}>
				<Typography>Loading submission…</Typography>
			</Box>
		);
	}

	return (
		<Stack width="1200px" margin="auto" pt={2} spacing={3} pb={6}>
			<Button startIcon={<KeyboardBackspaceRounded />} variant="outlined" onClick={handleBack} sx={{ mb: 1, width: 120 }}>
				Stores
			</Button>

			<Breadcrumbs sx={{ width: "100%" }}>
				<Link
					component="button"
					underline="hover"
					color="inherit"
					onClick={handleBack}
					sx={{ border: 0, background: "none", cursor: "pointer", fontSize: 14 }}
				>
					{plan?.name || "Plan"}
				</Link>
				<Typography color="text.secondary" fontSize={14}>
					{store?.store_name}
				</Typography>
				<Stack direction="row" spacing={1} alignItems="center">
					<Typography color="text.primary" fontWeight={700} fontSize={15}>
						Submission
					</Typography>
					<Chip
						label={submissionData?.status || "DRAFT"}
						size="small"
						color={submissionData?.status === "APPROVED" ? "success" : submissionData?.status === "SUBMITTED" ? "warning" : "default"}
					/>
					{submissionData?.is_final && <Chip label="FINAL" size="small" color="success" />}
				</Stack>
			</Breadcrumbs>

			{/* Actions + Approvals bar — always visible */}
			<Stack
				direction="row"
				spacing={1}
				alignItems="center"
				flexWrap="wrap"
				useFlexGap
				sx={{
					width: "100%",
					boxSizing: "border-box",
					bgcolor: grey[50],
					border: `1px solid ${grey[200]}`,
					borderRadius: 3,
					p: 1.5,
				}}
			>
				{submissionData?.created_by && (
					<>
						<Typography variant="body2" fontWeight={600} color={grey[700]}>
							Created by:
						</Typography>
						<Typography variant="body2" color={grey[800]}>
							{submissionData.created_by_name || submissionData.created_by}
							{submissionData.created_by_role ? (
								<Box component="span" sx={{ color: grey[500], ml: 0.75 }}>
									({submissionData.created_by_role})
								</Box>
							) : null}
						</Typography>
						<Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
					</>
				)}
				<Typography variant="body2" fontWeight={600} color={grey[700]}>
					Approvals:
				</Typography>
				{isDraft ? (
					<Typography variant="caption" color="text.secondary">
						Submit to start peer approvals
					</Typography>
				) : hasApprovals ? (
					<>
						{approvedRoles.map((role) => (
							<Chip key={role} label={role} size="small" color="success" variant="outlined" icon={<CheckCircleIcon />} />
						))}
						<IconButton
							size="small"
							aria-label="Approval details"
							onClick={(e) => setApprovalInfoAnchor(e.currentTarget)}
						>
							<InfoOutlinedIcon fontSize="small" />
						</IconButton>
					</>
				) : (
					<Typography variant="caption" color="text.secondary">
						No approvals yet
					</Typography>
				)}
				<Box flexGrow={1} />
				{isOwner && isSubmitted && hasApprovals && !submissionData?.is_final && (
					<Typography variant="caption" color="text.secondary">
						Remove approvals before unsubmit
					</Typography>
				)}
				{canEdit && (
					<Button variant="contained" color="primary" onClick={handleSubmit} sx={{ px: 3, minWidth: 110 }}>
						Submit
					</Button>
				)}
				{canUnsubmit && (
					<Button variant="outlined" color="warning" onClick={handleCancel} sx={{ px: 2.5, minWidth: 110 }}>
						Unsubmit
					</Button>
				)}
				{canApproveHere && (
					<Button
						variant={approvals.some((s) => s.user_email === user?.email) ? "outlined" : "contained"}
						color={approvals.some((s) => s.user_email === user?.email) ? "error" : "success"}
						onClick={handleApproveToggle}
						sx={{ px: 2.5 }}
					>
						{approvals.some((s) => s.user_email === user?.email) ? "Remove Approval" : "Approve"}
					</Button>
				)}
			</Stack>

			<Popover
				open={Boolean(approvalInfoAnchor)}
				anchorEl={approvalInfoAnchor}
				onClose={() => setApprovalInfoAnchor(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
			>
				<Box sx={{ p: 2, minWidth: 280, maxWidth: 360 }}>
					<Typography variant="subtitle2" fontWeight={700} mb={1}>
						Approval details
					</Typography>
					<Stack spacing={1} mb={1.5}>
						{approvals.map((a) => (
							<Stack key={a.id} direction="row" spacing={1} alignItems="center">
								<Chip label={a.user_role || "—"} size="small" color="success" variant="outlined" />
								<Typography variant="body2">{displayNameFromEmail(a.user_name, a.user_email)}</Typography>
							</Stack>
						))}
					</Stack>
					<Divider sx={{ my: 1.5 }} />
					<Typography variant="subtitle2" fontWeight={700} mb={1}>
						Targets summary
					</Typography>
					{(() => {
						const swd = submissionData?.seasonWiseData || [];
						const newT = swd.find((r) => r.is_new_target) || {};
						const derT = swd.find((r) => !r.is_new_target) || {};
						return (
							<Stack spacing={1}>
								<Box>
									<Typography variant="caption" color="text.secondary" fontWeight={600}>
										NEW TARGET
									</Typography>
									<Typography variant="body2">
										Rev {toInLakhs(newT.revenue)}L · Qty {newT.qty || 0} · DIS {round1dp(newT.dis || 0)}% · ASP{" "}
										{round1dp(newT.asp || 0)}
									</Typography>
								</Box>
								<Box>
									<Typography variant="caption" color="text.secondary" fontWeight={600}>
										DERIVED TARGET
									</Typography>
									<Typography variant="body2">
										Rev {toInLakhs(derT.revenue)}L · Qty {derT.qty || 0} · DIS {round1dp(derT.dis || 0)}% · ASP{" "}
										{round1dp(derT.asp || 0)}
									</Typography>
								</Box>
							</Stack>
						);
					})()}
				</Box>
			</Popover>

			{/* Info Panel */}
			<Box display="flex" width="100%" columnGap={2.5} alignItems="flex-start" sx={infoPanelSx}>
				{/* Left: Store Info — single column */}
				<Stack
					spacing={1.25}
					sx={{
						width: 320,
						minWidth: 300,
						maxWidth: 340,
						flexShrink: 0,
						pr: 2,
						borderRight: `1px solid ${grey[200]}`,
					}}
				>
					<Typography sx={{ ...textInfo, fontSize: 15 }}>
						Store Name :
						<span style={{ fontSize: 16, fontWeight: "bold", color: grey[900], display: "block", lineHeight: 1.3 }}>
							{store.store_name} - {store.brand}
						</span>
					</Typography>
					<Typography sx={{ ...textInfo, fontSize: 15 }}>
						Store Code : <span style={{ ...infoMetricValueSx, fontSize: 16 }}>{store.mapping_code}</span>
					</Typography>
					<Typography sx={{ ...textInfo, fontSize: 15 }}>
						Age : <span style={{ ...infoMetricValueSx, fontSize: 16 }}>{calculateStoreAge(store.open_date)}</span>Y
					</Typography>
					<Typography sx={{ ...textInfo, fontSize: 15 }}>
						Store Grade : <span style={{ ...infoMetricValueSx, fontSize: 16 }}>{store.store_grade}</span>
					</Typography>
					<Typography sx={{ ...textInfo, fontSize: 15 }}>
						Area : <span style={{ ...infoMetricValueSx, fontSize: 16 }}>{store.area}</span> sq.ft
					</Typography>
					<Typography sx={{ ...textInfo, fontSize: 15 }}>
						SPSF : <span style={{ ...infoMetricValueSx, fontSize: 16 }}>Rs. {spsf}</span>
						<span style={{ fontSize: 14 }}> /M</span>
					</Typography>
					<Typography sx={{ ...textInfo, fontSize: 15 }}>
						City : <span style={{ ...infoMetricValueSx, fontSize: 16 }}>{store.city_tier}</span>
					</Typography>

					<Box sx={{ height: "1px", backgroundColor: grey[200], width: "100%" }} />

					<Box sx={{ ...textInfo, fontSize: 15 }} display="flex" alignItems="center" flexWrap="wrap" columnGap={0.5}>
						<span>6M Growth</span>
						<IconButton
							size="small"
							aria-label="6M growth info"
							onClick={(e) => setGwInfoAnchor(e.currentTarget)}
							sx={{ p: 0.25, color: grey[500] }}
						>
							<InfoOutlinedIcon sx={{ fontSize: 16 }} />
						</IconButton>
						<span>:</span>
						<span style={{ fontSize: 16, fontWeight: "bold", color: growthToPrvSeason > 0 ? green.A700 : red.A700 }}>
							{growthToPrvSeason}%
						</span>
					</Box>
					<Popover
						open={Boolean(gwInfoAnchor)}
						anchorEl={gwInfoAnchor}
						onClose={() => setGwInfoAnchor(null)}
						anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
						transformOrigin={{ vertical: "top", horizontal: "left" }}
					>
						<Box sx={{ p: 2, minWidth: 280, maxWidth: 360 }}>
							<Typography variant="subtitle2" fontWeight={700} mb={1}>
								6M Growth
							</Typography>
							<Typography variant="body2" color="text.secondary" mb={1.5}>
								Rolling revenue growth — last 180 days vs the prior 180 days. Not tied to SS/AW season boundaries.
							</Typography>
							<Stack spacing={1.25}>
								<Box>
									<Typography variant="caption" color="text.secondary" fontWeight={600}>
										LAST 6 MONTHS
									</Typography>
									<Typography variant="body2">
										{gwWindowDates.last6m.from} → {gwWindowDates.last6m.to}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Rev: {toInLakhs(sixMRaw?.nsv)}L
									</Typography>
								</Box>
								<Box>
									<Typography variant="caption" color="text.secondary" fontWeight={600}>
										PRIOR 6 MONTHS
									</Typography>
									<Typography variant="body2">
										{gwWindowDates.prv6m.from} → {gwWindowDates.prv6m.to}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Rev: {toInLakhs(prvLtlRaw?.nsv)}L
									</Typography>
								</Box>
								<Divider />
								<Typography variant="body2">
									Growth:{" "}
									<span style={{ fontWeight: 700, color: growthToPrvSeason > 0 ? green.A700 : red.A700 }}>
										{growthToPrvSeason}%
									</span>
								</Typography>
							</Stack>
						</Box>
					</Popover>
					<Typography sx={{ ...textInfo, fontSize: 15 }} component="div">
						Repeat Customers :{" "}
						{repeatCustStats ? (
							<Tooltip
								arrow
								placement="right"
								title={
									<Box sx={{ py: 0.5 }}>
										<Typography fontSize={12} fontWeight={700} mb={0.5}>
											Last 6 months
										</Typography>
										<Typography fontSize={12}>
											Repeat: {repeatCustStats.repeat.toLocaleString("en-IN")} ({repeatCustStats.repeatPct}%)
										</Typography>
										<Typography fontSize={12}>
											New: {repeatCustStats.neu.toLocaleString("en-IN")} ({repeatCustStats.newPct}%)
										</Typography>
										<Typography fontSize={12} mt={0.5} sx={{ opacity: 0.85 }}>
											Total: {repeatCustStats.total.toLocaleString("en-IN")}
										</Typography>
									</Box>
								}
							>
								<span style={{ ...infoMetricValueSx, fontSize: 16, cursor: "help" }}>{repeatCustomers}%</span>
							</Tooltip>
						) : (
							<span style={{ ...infoMetricValueSx, fontSize: 16 }}>{repeatCustomers}%</span>
						)}
					</Typography>
				</Stack>

				{/* Right: Season Info */}
				<SeasonInfoPanel
					ltlKpi={ltlKpi}
					lltlKpi={lltlKpi}
					prevLltlKpi={prevLltlKpi}
					sixMKpi={sixMKpi}
					prvSixMKpi={prvSixMKpi}
					targetData={targetData}
					derivedTarget={derivedTarget}
					canEdit={canEdit}
					onTargetFieldChange={handleTargetFieldChange}
					onSave={handleSaveTarget}
				/>
			</Box>

			{/* Sales Projection */}
			<Stack direction="row" alignItems="center" spacing={0.5}>
				<Typography variant="h5">Sales Projection</Typography>
				<IconButton
					size="small"
					aria-label="Sales projection info"
					onClick={() => setProjectionInfoOpen(true)}
					sx={{ color: grey[600] }}
				>
					<InfoOutlinedIcon fontSize="small" />
				</IconButton>
			</Stack>
			<SalesProjectionTable
				season={season}
				planSeasonLabel={plan?.name}
				ltlSeasonLabel={historicalData?.ltlSeason}
				lltlSeasonLabel={historicalData?.lltlSeason}
				monthlyLtl={monthlyLtl}
				monthlyLltl={monthlyLltl}
				monthlyPrevLltl={monthlyPrevLltl}
				ltlKpi={ltlKpi}
				projData={projData}
				canEdit={canEdit}
				onMonthChange={handleMonthChange}
			/>

			<Dialog
				open={projectionInfoOpen}
				onClose={() => setProjectionInfoOpen(false)}
				maxWidth="sm"
				fullWidth
				PaperProps={{ sx: { borderRadius: 2 } }}
			>
				<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
					<Typography variant="h6" fontWeight={700}>
						Sales Projection — what you see
					</Typography>
					<IconButton size="small" onClick={() => setProjectionInfoOpen(false)} aria-label="Close">
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>
				<DialogContent dividers sx={{ bgcolor: grey[50] }}>
					<Stack spacing={2.5} alignItems="stretch">
						{/* History month cell example */}
						<Box>
							<Typography variant="subtitle2" fontWeight={700} mb={1}>
								History month (read-only)
							</Typography>
							<Box
								display="flex"
								justifyContent="center"
								sx={{
									bgcolor: "#fafafa",
									border: `1px solid ${grey[300]}`,
									borderRadius: 1,
									py: 1,
									mb: 1.25,
								}}
							>
								<Box sx={{ width: 140 }}>
									<MonthValueCell value={12.5} growth={8.2} repeatPct={64.3} />
								</Box>
							</Box>
							<Stack spacing={0.75} pl={0.5}>
								<Typography fontSize={13} color={grey[700]}>
									<strong>12.5</strong> — monthly revenue (lakhs)
								</Typography>
								<Typography fontSize={13} color={grey[700]}>
									<strong>8.2%</strong> — growth vs previous comparable month
								</Typography>
								<Typography fontSize={13} color={grey[700]}>
									<strong>R 64.3%</strong> — repeat customers that month
								</Typography>
							</Stack>
						</Box>

						{/* NEW / editable month cell example */}
						<Box>
							<Typography variant="subtitle2" fontWeight={700} mb={1}>
								Plan month (you fill)
							</Typography>
							<Box
								display="flex"
								justifyContent="center"
								sx={{
									border: `1px solid ${red[900]}`,
									borderRadius: 1,
									py: 1,
									mb: 1.25,
									bgcolor: "#fff8f8",
								}}
							>
								<Box sx={{ width: 140 }}>
									<MonthValueCell growth={5.0}>
										<NumberTextField
											disableHelperText
											size="small"
											value="14.0"
											sx={{ width: 68, "& .MuiInputBase-input": { py: 0.5, fontSize: 14 } }}
											inputProps={{ style: { textAlign: "center" }, readOnly: true }}
										/>
									</MonthValueCell>
								</Box>
							</Box>
							<Stack spacing={0.75} pl={0.5}>
								<Typography fontSize={13} color={grey[700]}>
									<strong>Input</strong> — enter planned revenue in lakhs (DRAFT only)
								</Typography>
								<Typography fontSize={13} color={grey[700]}>
									<strong>% below</strong> — growth vs LTL month for that column
								</Typography>
								<Typography fontSize={13} color={grey[700]}>
									<strong>TOTAL</strong> — auto-sum of your month inputs
								</Typography>
							</Stack>
						</Box>
					</Stack>
				</DialogContent>
				<DialogActions sx={{ px: 3, py: 1.5 }}>
					<Button variant="contained" onClick={() => setProjectionInfoOpen(false)}>
						Got it
					</Button>
				</DialogActions>
			</Dialog>

			{/* Category projection */}
			<Stack spacing={1.5} pt={2}>
				<Typography variant="h5" fontWeight={700}>
					Category projection
				</Typography>

				<Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
					<Box
						sx={{
							width: 360,
							bgcolor: grey[100],
							border: `1px solid ${grey[200]}`,
							borderRadius: 2,
							px: 0.5,
							pt: 0.5,
							pb: 0.25,
						}}
					>
						<Tabs
							value={activeDivision}
							onChange={(_, v) => setActiveDivision(v)}
							variant="fullWidth"
							sx={{
								minHeight: 44,
								bgcolor: "transparent",
								"& .MuiTabs-flexContainer": {
									gap: 0.25,
								},
								"& .MuiTabs-indicator": { display: "none" },
								"& .MuiTab-root": {
									minHeight: 40,
									minWidth: 0,
									flex: 1,
									textTransform: "none",
									borderRadius: 1.5,
									color: grey[600],
									bgcolor: "transparent",
									fontWeight: 600,
									px: 0.75,
									py: 0.25,
									opacity: 1,
									"&.Mui-selected": {
										bgcolor: "#fff",
										color: grey[900],
										boxShadow: `0 1px 2px ${grey[300]}`,
									},
								},
							}}
						>
							{divisionData.map((d) => (
								<Tab
									key={d.key}
									value={d.key}
									disableRipple
									label={
										<Stack spacing={0.15} alignItems="center">
											<Typography component="span" fontSize={13} fontWeight={700} lineHeight={1.2} color="inherit">
												{d.name}
											</Typography>
											<Typography component="span" fontSize={12} fontWeight={600} lineHeight={1.2} color="inherit" sx={{ opacity: 0.75 }}>
												{divisionContribution[d.key] || 0}%
											</Typography>
										</Stack>
									}
								/>
							))}
						</Tabs>
					</Box>

					<Typography sx={{ ...textInfo, whiteSpace: "nowrap", fontWeight: 600 }}>
						completed {submissionData?.current_count || 0}/{submissionData?.total_count || 0}
					</Typography>
				</Stack>
			</Stack>

			<CategoryView
				submissionId={activeSubmissionId}
				division={activeDivision}
				categories={filteredCategories}
				loading={categoriesLoading}
				canEdit={canEdit}
				onDataChanged={refreshAll}
			/>
		</Stack>
	);
};

export default StoreBp;
